import { ChartModel, ChartTip, Pt, areaPath, edgeX, groupCenters, linePath, monotonePath, niceTicks, percentages, scaleY, slotX } from './chart-geometry.util'
import { Burnup, BugStats, MetricsArea, SprintVelocity, VelocityTimeline, WorklogMetrics } from '@modules/private/worklog/data/interfaces/metrics.interface'
import { ORIGIN_META, SEVERITY_META, STATUS_META, TYPE_META } from '@modules/private/worklog/data/constants/worklog.constants'
import { WorkItemSeverity } from '@modules/private/worklog/data/interfaces/worklog.interface'

export type Tone = 'neutral' | 'ok' | 'warn' | 'danger'

export interface KpiTile {
  key: string
  label: string
  value: string
  unit: string | null
  sub: string
  hint: string
  icon: string
  /** color del ícono (token del panel), no del valor */
  accent: 'blue' | 'green' | 'amber' | 'red' | 'purple'
  tone: Tone
  /** avance 0–100 cuando la cifra es «de un total» */
  meter: number | null
}

export interface HBarRow {
  key: string
  label: string
  value: number
  valueLabel: string
  share: string | null
  /** ancho relativo al mayor de la lista (0–100) */
  width: number
  color: string
  dashed: boolean
}

export interface MeterVM {
  label: string
  segments: { key: string; label: string; value: number; pct: number; color: string }[]
  total: number
}

export interface InvestmentVM {
  total: number
  fronts: HBarRow[]
  note: string
  estimatedPoints: number
  type: MeterVM
  origin: MeterVM
}

export interface StageVM {
  status: string
  label: string
  icon: string
  color: string
  now: number
  oldest: string
  oldestTone: Tone
  avg: string
  avgWidth: number
  samples: string
}

export interface DashboardVM {
  kpis: KpiTile[]
  velocity: { chart: ChartModel; caption: string; note: string } | null
  sprint: {
    title: string
    window: string
    delivered: number
    committed: number | null
    progress: number
    chart: ChartModel | null
    facts: { label: string; value: string; tone: Tone; icon: string }[]
    emptyText: string | null
  } | null
  investment: { periods: { key: string; label: string; data: InvestmentVM }[] }
  flow: { stages: StageVM[]; steps: { label: string; value: string; detail: string }[]; total: string | null }
  quality: { chart: ChartModel | null; legend: { label: string; kind: 'reported' | 'resolved' }[]; severity: HBarRow[]; resolution: string; open: number }
  health: { chips: { key: string; label: string; value: string; tone: Tone; review: boolean }[]; pending: { id: string; code: string; title: string; detail: string }[] }
}

// ─────────────────────────── Formato ───────────────────────────

//* Fechas civiles (`YYYY-MM-DD`) a mano: `new Date('2026-09-28')` es medianoche UTC y en CDMX se
//* pinta el 27. Los meses como los escribe es-MX («sept»), igual que el resto del panel.
const MONTH_LABELS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic']
const WEEKDAY_LABELS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb']

/** `28 sept` a partir de `2026-09-28`. */
export function civilLabel(civil: string): string {
  const [, m, d] = civil.split('-').map(Number)
  return `${d} ${MONTH_LABELS[m - 1]}`
}

function civilWeekday(civil: string): string {
  const [y, m, d] = civil.split('-').map(Number)
  return WEEKDAY_LABELS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]
}

const nf = new Intl.NumberFormat('es-MX')
const n = (v: number): string => nf.format(v)
const daysLabel = (v: number | null | undefined): string => (v == null ? '—' : `${v} d`)
const plural = (k: number, singular: string, pluralForm: string): string => `${n(k)} ${k === 1 ? singular : pluralForm}`
/** Ancho de barra relativo al mayor; un valor chico se ve (2 %), un cero no deja rastro. */
const barWidth = (v: number, max: number): number => (v > 0 ? Math.max(2, Math.round((v / max) * 100)) : 0)
/** «Sin clasificar» y lo que no tiene color propio van en el gris del Backlog, no en uno inventado. */
const NEUTRAL_COLOR = STATUS_META.Backlog.color

// ─────────────────────────── Builder ───────────────────────────

/**
 * Modelo de vista del dashboard de Métricas de TI (F-156). Se llama UNA vez al llegar las métricas y
 * la plantilla solo lee `vm.*`: ninguna expresión de la plantilla calcula ni crea objetos en cada
 * detección de cambios, que es lo que trabó la pantalla en B-062.
 *
 * TI es una sola persona: nada aquí habla de «equipo».
 */
export function buildDashboardVM(m: WorklogMetrics): DashboardVM {
  const closed = m.velocity.filter((v) => v.closed)
  const active = m.velocity.find((v) => !v.closed && m.kpis.currentSprint != null && v.sprint === m.kpis.currentSprint) ?? null
  return {
    kpis: buildKpis(m, closed),
    velocity: m.velocityTimeline?.points?.length ? buildVelocity(m.velocityTimeline) : null,
    sprint: buildSprint(m),
    investment: buildInvestment(m, closed, active),
    flow: buildFlow(m),
    quality: buildQuality(m.bugs),
    health: buildHealth(m),
  }
}

// ─────────────────────────── KPIs ───────────────────────────

function buildKpis(m: WorklogMetrics, closed: SprintVelocity[]): KpiTile[] {
  const s = m.sprintSummary
  const b = m.burnup
  const committed = s?.committedPoints ?? null
  const delivered = s?.deliveredPoints ?? m.kpis.pointsThisSprint ?? 0
  const dayLabel = b && b.todayIndex >= 0 ? `día ${Math.min(b.todayIndex + 1, b.days.length)} de ${b.days.length}` : null

  const points = closed.map((v) => v.points)
  const range = points.length ? `${n(Math.min(...points))}–${n(Math.max(...points))}` : null
  const sprintRange = closed.length ? `${closed.length === 1 ? 'Sprint' : 'Sprints'} ${closed[0].sprint}${closed.length > 1 ? '–' + closed[closed.length - 1].sprint : ''}` : ''

  const toDeploy = m.stageTimes.find((st) => st.status === 'To Deploy')
  const oldestDays = toDeploy?.openNow ? Math.round(toDeploy.openMaxDays) : null
  const criticalCount = m.bugs.openBySeverity?.critical ?? null

  return [
    {
      key: 'sprint',
      label: s ? `Puntos del Sprint ${s.sprint.number}` : 'Puntos del sprint',
      value: s ? n(delivered) : '—',
      unit: committed != null ? `/${n(committed)}` : null,
      sub: s ? [dayLabel, 'provisional'].filter(Boolean).join(' · ') : 'No hay sprint en curso',
      hint: 'Puntos entregados en el sprint en curso contra lo que se comprometió al activarlo. Sube conforme las tareas llegan a To Deploy.',
      icon: 'fa-solid fa-flag',
      accent: 'blue',
      tone: 'neutral',
      meter: committed ? Math.min(100, Math.round((delivered / committed) * 100)) : null,
    },
    {
      key: 'avg',
      label: 'Promedio por sprint',
      value: closed.length ? n(Math.round(m.kpis.avgPoints)) : '—',
      unit: closed.length ? 'pts' : null,
      sub: closed.length ? `${sprintRange} · rango ${range}` : 'Aún no cierra ningún sprint',
      hint: 'Media de puntos entregados en los sprints ya cerrados. El sprint en curso no entra: va a la mitad.',
      icon: 'fa-solid fa-gauge-high',
      accent: 'green',
      tone: 'neutral',
      meter: null,
    },
    {
      key: 'pending',
      label: 'Terminadas sin liberar',
      value: n(m.kpis.pendingRelease),
      unit: null,
      sub: oldestDays != null ? `la más antigua lleva ${plural(oldestDays, 'día', 'días')}` : 'nada esperando deploy',
      hint: 'Tareas en To Deploy: el desarrollo terminó y esperan salir a producción. Al hacer el deploy pasan a Terminadas.',
      icon: 'fa-solid fa-rocket',
      accent: 'amber',
      tone: oldestDays != null && oldestDays >= 7 ? 'warn' : 'neutral',
      meter: null,
    },
    {
      key: 'bugs',
      label: 'Bugs abiertos',
      value: n(m.bugs.open),
      unit: null,
      sub: [criticalCount != null ? plural(criticalCount, 'crítico', 'críticos') : null, m.bugs.avgResolutionDays != null ? `${m.bugs.avgResolutionDays} d de resolución media` : null]
        .filter(Boolean)
        .join(' · '),
      hint: 'Bugs reportados que aún no se entregan. La resolución media va de que se arranca un bug a que se entrega.',
      icon: 'fa-solid fa-bug',
      accent: 'red',
      tone: criticalCount ? 'danger' : 'neutral',
      meter: null,
    },
  ]
}

// ─────────────────────────── Velocidad ───────────────────────────

function buildVelocity(t: VelocityTimeline): DashboardVM['velocity'] {
  const pts = t.points
  const total = pts.length
  const current = pts.find((p) => p.state === 'provisional') ?? null
  const max = Math.max(...pts.map((p) => Math.max(p.points, p.projectedPoints ?? 0)), 1)
  const { max: top, ticks } = niceTicks(max)
  const xs = pts.map((_, i) => slotX(i, total))

  const closedPts: Pt[] = []
  pts.forEach((p, i) => {
    if (p.state === 'closed') closedPts.push({ x: xs[i], y: scaleY(p.points, top) })
  })

  const paths: ChartModel['paths'] = []
  if (closedPts.length) {
    paths.push({ d: areaPath(closedPts), kind: 'area' })
    paths.push({ d: monotonePath(closedPts), kind: 'line' })
  }
  const bars: ChartModel['bars'] = []
  const dots: ChartModel['dots'] = []
  const labelAll = total <= 14

  pts.forEach((p, i) => {
    if (p.state === 'closed') dots.push({ x: xs[i], y: scaleY(p.points, top), kind: 'hollow', label: labelAll ? n(p.points) : null, muted: false })
  })

  //* El sprint en curso no se dibuja como una caída de la línea a sus puntos parciales: la línea se
  //* proyecta punteada hacia lo comprometido, y una barra muestra cuánto va.
  if (current) {
    const i = pts.indexOf(current)
    const last = closedPts[closedPts.length - 1]
    if (current.projectedPoints != null) {
      const yProjected = scaleY(current.projectedPoints, top)
      if (last) paths.push({ d: linePath([last, { x: xs[i], y: yProjected }]), kind: 'projected' })
      dots.push({ x: xs[i], y: yProjected, kind: 'projected', label: n(current.projectedPoints), muted: true })
      bars.push({ x: xs[i] - 1.2, y: yProjected, w: 2.4, h: 100 - yProjected, kind: 'track' })
    }
    const yCurrent = scaleY(current.points, top)
    bars.push({ x: xs[i] - 1.2, y: yCurrent, w: 2.4, h: 100 - yCurrent, kind: 'progress' })
  }

  //* Hasta agosto los puntos salían del git log; desde el Sprint 1, del Tablero. Se marca para que el
  //* cambio de método no se lea como cambio de ritmo.
  const vlines: ChartModel['vlines'] = []
  const firstBoardIndex = t.firstBoardKey ? pts.findIndex((p) => p.key === t.firstBoardKey) : -1
  if (firstBoardIndex > 0) vlines.push({ x: (xs[firstBoardIndex - 1] + xs[firstBoardIndex]) / 2, label: 'Tablero', kind: 'divider' })

  const slots = pts.map((p, i) => ({ x: xs[i], w: 100 / total, tip: velocityTip(p) }))
  const caption = [
    `${n(t.accumulatedPoints)} pts acumulados`,
    current
      ? `Sprint ${current.sprint?.number}: ${n(current.points)} medidos de ${current.projectedPoints != null ? n(current.projectedPoints) + ' comprometidos' : 'un compromiso sin estimar'}${current.sprint ? ', cierra el ' + civilLabel(current.sprint.end) : ''}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return {
    chart: {
      ariaLabel: `Puntos entregados por quincena desde ${pts[0].month} ${pts[0].year}`,
      yTicks: ticks.map((v) => ({ y: scaleY(v, top), label: n(v) })),
      xTicks: pts.map((p, i) => ({ x: xs[i], label: p.label })),
      xGroups: groupCenters(
        pts.map((p) => `${p.month}`),
        xs,
      ),
      paths,
      vlines,
      hlines: [],
      bars,
      dots,
      slots,
    },
    caption,
    note: 'Hasta agosto, valores presentados en la junta (estimados del git log). Desde el Sprint 1, medidos en el Tablero.',
  }
}

function velocityTip(p: VelocityTimeline['points'][number]): ChartTip {
  if (p.source === 'history') return { title: `${p.month} ${p.label} · ${n(p.points)} pts`, lines: ['Valor presentado en la junta', 'Estimado del git log, antes del Tablero'] }
  const s = p.sprint!
  const dateRange = `${civilLabel(s.start)} → ${civilLabel(s.end)}`
  if (p.state === 'provisional') {
    return {
      title: `Sprint ${s.number} en curso · ${dateRange}`,
      lines: [
        `${n(p.points)} pts a hoy${p.projectedPoints != null ? ` de ${n(p.projectedPoints)} comprometidos` : ''}`,
        `${plural(s.count, 'entrega', 'entregas')} · ${n(s.features)} features · ${n(s.bugs)} bugs`,
      ],
    }
  }
  return {
    title: `Sprint ${s.number} · ${dateRange}`,
    lines: [
      `${n(p.points)} pts · ${plural(s.count, 'entrega', 'entregas')}`,
      `${n(s.features)} features · ${n(s.bugs)} bugs`,
      s.committedPoints != null ? `Comprometido al activarlo: ${n(s.committedPoints)}` : 'Arrancó sin compromiso estimado',
    ],
  }
}

// ─────────────────────────── Sprint en curso ───────────────────────────

function buildSprint(m: WorklogMetrics): DashboardVM['sprint'] {
  const s = m.sprintSummary
  if (!s) return null
  const b = m.burnup ?? null
  const committed = s.committedPoints
  const delivered = s.deliveredPoints
  const facts: NonNullable<DashboardVM['sprint']>['facts'] = [
    { label: 'Falta por entregar', value: plural(s.openCount, 'tarea', 'tareas'), tone: 'neutral', icon: 'fa-solid fa-list-check' },
    { label: 'Esperando liberación', value: plural(s.pendingRelease, 'tarea', 'tareas'), tone: 'neutral', icon: 'fa-solid fa-rocket' },
  ]
  if (s.unestimated) facts.push({ label: 'Sin estimar', value: plural(s.unestimated, 'tarea', 'tareas'), tone: 'warn', icon: 'fa-solid fa-triangle-exclamation' })

  return {
    title: `Sprint ${s.sprint.number} en curso`,
    window: b ? `${civilLabel(b.startDate)} → ${civilLabel(b.endDate)}` : '',
    delivered,
    committed,
    progress: committed ? Math.min(100, Math.round((delivered / committed) * 100)) : 0,
    chart: b && b.days.length > 1 ? buildBurnupChart(b) : null,
    facts,
    emptyText: b ? null : 'El sprint no tiene días que dibujar todavía.',
  }
}

function buildBurnupChart(b: Burnup): ChartModel {
  const total = b.days.length
  const committed = b.committedPoints ?? 0
  const maxCumulative = Math.max(...b.days.map((d) => d.cumulative ?? 0), 0)
  const { max: top, ticks } = niceTicks(Math.max(committed, maxCumulative, 1))
  const xs = b.days.map((_, i) => edgeX(i, total))

  const actualPts: Pt[] = []
  b.days.forEach((d, i) => {
    if (d.cumulative != null) actualPts.push({ x: xs[i], y: scaleY(d.cumulative, top) })
  })
  const ideal: Pt[] = b.days.filter((d) => d.ideal != null).map((d, i) => ({ x: xs[i], y: scaleY(d.ideal as number, top) }))

  const paths: ChartModel['paths'] = []
  if (ideal.length > 1) paths.push({ d: linePath(ideal), kind: 'ideal' })
  if (actualPts.length) {
    //* Tramos rectos de un día al siguiente, no curva suavizada: una curva insinuaría avance entre
    //* entregas que no existió.
    paths.push({ d: areaPath(actualPts, false), kind: 'area' })
    paths.push({ d: linePath(actualPts), kind: 'line' })
  }

  const last = actualPts.length - 1
  const dots: ChartModel['dots'] = actualPts.length ? [{ x: actualPts[last].x, y: actualPts[last].y, kind: 'solid', label: n(b.days[last].cumulative as number), muted: false }] : []

  return {
    ariaLabel: `Puntos entregados acumulados del Sprint ${b.sprint} contra el ritmo ideal`,
    yTicks: ticks.map((v) => ({ y: scaleY(v, top), label: n(v) })),
    xTicks: b.days.map((d, i) => ({ x: xs[i], label: d.date.slice(8).replace(/^0/, '') })),
    xGroups: groupCenters(
      b.days.map((d) => MONTH_LABELS[Number(d.date.slice(5, 7)) - 1]),
      xs,
    ),
    paths,
    vlines: b.todayIndex >= 0 && b.todayIndex < total - 1 ? [{ x: xs[b.todayIndex], label: 'hoy', kind: 'today' }] : [],
    hlines: b.committedPoints ? [{ y: scaleY(committed, top), label: `Compromiso ${n(committed)}` }] : [],
    bars: [],
    dots,
    slots: b.days.map((d, i) => ({
      x: xs[i],
      w: 100 / total,
      tip: {
        title: `${civilWeekday(d.date)} ${civilLabel(d.date)}`,
        lines: [
          d.cumulative != null ? `Entregado ese día: ${n(d.delivered)} pts` : 'Todavía no llega',
          d.cumulative != null ? `Acumulado: ${n(d.cumulative)}${b.committedPoints ? ' de ' + n(committed) : ''}` : '',
          d.ideal != null ? `Ritmo ideal: ${n(Math.round(d.ideal))}` : '',
        ].filter(Boolean),
      },
    })),
  }
}

// ─────────────────────────── En qué se invirtió ───────────────────────────

function buildInvestment(m: WorklogMetrics, closed: SprintVelocity[], active: SprintVelocity | null): DashboardVM['investment'] {
  const periods: DashboardVM['investment']['periods'] = []
  if (active) periods.push({ key: 'actual', label: `Sprint ${active.sprint} · en curso`, data: investmentOf([active], m.areas) })
  const previous = closed[closed.length - 1]
  if (previous) periods.push({ key: 'anterior', label: `Sprint ${previous.sprint}`, data: investmentOf([previous], m.areas) })
  const all = [...closed, ...(active ? [active] : [])]
  if (all.length) periods.push({ key: 'todo', label: `Desde el Sprint ${all[0].sprint}`, data: investmentOf(all, m.areas) })
  return { periods }
}

function investmentOf(sprints: SprintVelocity[], areas: MetricsArea[]): InvestmentVM {
  const total = sprints.reduce((a, v) => a + v.points, 0)
  const estimated = sprints.reduce((a, v) => a + (v.splitEstimatedPoints ?? 0), 0)
  const pointsForArea = (key: string) => sprints.reduce((a, v) => a + (v.pointsByArea?.[key] ?? 0), 0)

  const rows = areas.map((ar) => ({ ar, value: pointsForArea(ar.key) })).filter(({ ar, value }) => ar.reportable || value > 0)
  const maxRow = Math.max(...rows.map((f) => f.value), 1)
  const shares = percentages(rows.map((f) => f.value))
  const fronts: HBarRow[] = rows.map(({ ar, value }, i) => ({
    key: ar.key,
    label: ar.reportable ? ar.name : `${ar.name} · fuera del reporte`,
    value,
    valueLabel: plural(value, 'pt', 'pts'),
    share: total ? `${shares[i]}%` : null,
    width: barWidth(value, maxRow),
    color: ar.color,
    dashed: false,
  }))

  const features = sprints.reduce((a, v) => a + v.features, 0)
  const bugs = sprints.reduce((a, v) => a + v.bugs, 0)
  const proposed = sprints.reduce((a, v) => a + (v.proposed ?? 0), 0)
  const count = sprints.reduce((a, v) => a + v.count, 0)

  return {
    total,
    fronts,
    estimatedPoints: estimated,
    note: total ? `Suman los ${n(total)} pts del periodo${estimated ? ` · ${n(estimated)} con reparto estimado` : ''}` : 'Sin entregas en el periodo',
    //* Colores y nombres del catálogo del Tablero: el mismo bug se pinta igual aquí y en su detalle.
    type: meter('Tipo de trabajo', [
      { key: 'task', label: `${TYPE_META.task.label}s`, value: features, color: TYPE_META.task.color },
      { key: 'bug', label: `${TYPE_META.bug.label}s`, value: bugs, color: TYPE_META.bug.color },
    ]),
    origin: meter('Origen', [
      { key: 'requested', label: `${ORIGIN_META.requested.label} por el negocio`, value: count - proposed, color: ORIGIN_META.requested.color },
      { key: 'proposed', label: `${ORIGIN_META.proposed.label} · iniciativa de TI`, value: proposed, color: ORIGIN_META.proposed.color },
    ]),
  }
}

function meter(label: string, items: { key: string; label: string; value: number; color: string }[]): MeterVM {
  const pcts = percentages(items.map((i) => i.value))
  return { label, total: items.reduce((a, i) => a + i.value, 0), segments: items.map((it, k) => ({ ...it, pct: pcts[k] })) }
}

// ─────────────────────────── Flujo ───────────────────────────

function buildFlow(m: WorklogMetrics): DashboardVM['flow'] {
  const maxAvg = Math.max(...m.stageTimes.map((s) => s.avgDays), 0.1)
  const stages: StageVM[] = m.stageTimes.map((s) => {
    const meta = (STATUS_META as Record<string, { label: string; color: string; icon: string }>)[s.status]
    const oldest = Math.round(s.openMaxDays)
    return {
      status: s.status,
      label: meta?.label ?? s.status,
      icon: meta?.icon ?? 'fa-solid fa-circle',
      color: meta?.color ?? NEUTRAL_COLOR,
      now: s.openNow,
      oldest: s.openNow ? plural(oldest, 'día', 'días') : '—',
      oldestTone: !s.openNow ? 'neutral' : oldest >= 14 ? 'danger' : oldest >= 7 ? 'warn' : 'ok',
      avg: s.samples ? daysLabel(s.avgDays) : '—',
      avgWidth: s.samples ? Math.round((s.avgDays / maxAvg) * 100) : 0,
      samples: s.samples ? plural(s.samples, 'recorrido', 'recorridos') : 'sin recorridos medidos',
    }
  })

  const f = m.flowTimes
  //* Los tramos se miden sobre grupos distintos de tareas (cada uno sobre las que tienen sus dos
  //* fechas), así que NO se dibujan como una suma: son tres esperas que se leen por separado.
  const steps = [
    { label: 'Espera para arrancar', value: daysLabel(f.avgWaitDays), detail: `del alta a que arranca · ${plural(f.waitTracked, 'tarea', 'tareas')}` },
    { label: 'Desarrollo', value: daysLabel(f.avgCycleDays), detail: `de que arranca a To Deploy · ${plural(f.cycleTracked, 'tarea', 'tareas')}` },
    { label: 'Espera de liberación', value: daysLabel(f.avgReleaseDays), detail: `de To Deploy a producción · ${plural(f.releaseTracked, 'tarea', 'tareas')}` },
  ]
  const total = f.avgTotalDays != null ? `De que arranca a producción: ${daysLabel(f.avgTotalDays)} en promedio (${plural(f.totalTracked ?? 0, 'tarea', 'tareas')})` : null
  return { stages, steps, total }
}

// ─────────────────────────── Calidad ───────────────────────────

type SeverityKey = WorkItemSeverity | 'unclassified'

/** Las severidades del catálogo del Tablero, de la más grave a la menor, más «Sin clasificar» al final. */
const SEVERITY_ROWS: { key: SeverityKey; label: string; color: string }[] = [
  ...(['critical', 'high', 'medium', 'low'] as WorkItemSeverity[]).map((key) => ({ key, ...SEVERITY_META[key] })),
  { key: 'unclassified', label: 'Sin clasificar', color: NEUTRAL_COLOR },
]

function buildQuality(b: BugStats): DashboardVM['quality'] {
  const sev = b.openBySeverity ?? { critical: 0, high: 0, medium: 0, low: 0, unclassified: 0 }
  const maxSev = Math.max(...SEVERITY_ROWS.map((s) => sev[s.key]), 1)
  const severity: HBarRow[] = SEVERITY_ROWS.map((s) => ({
    key: s.key,
    label: s.label,
    value: sev[s.key],
    valueLabel: n(sev[s.key]),
    share: null,
    width: barWidth(sev[s.key], maxSev),
    color: s.color,
    dashed: s.key === 'unclassified',
  }))

  const periods = b.byPeriod ?? []
  let chart: ChartModel | null = null
  if (periods.length) {
    const max = Math.max(...periods.map((p) => Math.max(p.reported, p.resolved)), 1)
    const { max: top, ticks } = niceTicks(max)
    const total = periods.length
    const barW = Math.min(9, 30 / total)
    const bars: ChartModel['bars'] = []
    periods.forEach((p, i) => {
      const x = slotX(i, total)
      const yReported = scaleY(p.reported, top)
      const yResolved = scaleY(p.resolved, top)
      bars.push({ x: x - barW - 0.4, y: yReported, w: barW, h: 100 - yReported, kind: 'reported' })
      bars.push({ x: x + 0.4, y: yResolved, w: barW, h: 100 - yResolved, kind: 'resolved' })
    })
    chart = {
      ariaLabel: 'Bugs reportados y resueltos por sprint',
      yTicks: ticks.map((v) => ({ y: scaleY(v, top), label: n(v) })),
      xTicks: periods.map((p, i) => ({ x: slotX(i, total), label: `Sprint ${p.sprint}` })),
      xGroups: [],
      paths: [],
      vlines: [],
      hlines: [],
      bars,
      dots: [],
      slots: periods.map((p, i) => ({
        x: slotX(i, total),
        w: 100 / total,
        tip: { title: `Sprint ${p.sprint}`, lines: [`Reportados: ${n(p.reported)}`, `Resueltos: ${n(p.resolved)}`] },
      })),
    }
  }

  return {
    chart,
    legend: [
      { label: 'Reportados', kind: 'reported' },
      { label: 'Resueltos', kind: 'resolved' },
    ],
    severity,
    resolution:
      b.avgResolutionDays != null ? `${b.avgResolutionDays} d de resolución media · ${plural(b.resolutionTracked, 'bug medido', 'bugs medidos')}` : 'Sin bugs con tiempo de resolución medible',
    open: b.open,
  }
}

// ─────────────────────────── Salud del dato ───────────────────────────

function buildHealth(m: WorklogMetrics): DashboardVM['health'] {
  const pending = m.splitPending ?? []
  const estimatedPts = pending.reduce((a, p) => a + p.points, 0)
  const s = m.sprintSummary
  const chips: DashboardVM['health']['chips'] = [
    {
      key: 'split',
      label: 'Entregas con reparto estimado',
      value: pending.length ? `${n(pending.length)} · ${n(estimatedPts)} pts` : '0',
      tone: pending.length ? 'warn' : 'ok',
      review: pending.length > 0,
    },
    { key: 'noDate', label: 'Terminadas sin fecha de entrega', value: n(m.kpis.deliveredWithoutDate), tone: m.kpis.deliveredWithoutDate ? 'warn' : 'ok', review: false },
    { key: 'noSeverity', label: 'Bugs sin severidad', value: n(m.bugs.unclassified), tone: m.bugs.unclassified ? 'warn' : 'ok', review: false },
    { key: 'noTime', label: 'Bugs sin tiempo medible', value: n(m.bugs.resolutionExcluded), tone: 'neutral', review: false },
  ]
  if (s && s.estimateTracked) chips.push({ key: 'estimate', label: 'Estimaciones clavadas', value: `${s.estimateAccuracyPct}% de ${n(s.estimateTracked)}`, tone: 'neutral', review: false })

  return {
    chips,
    pending: pending.map((p) => ({ id: p._id, code: p.code ?? '—', title: p.title, detail: `${n(p.points)} pts · ${p.areas.length} áreas` })),
  }
}
